// The linked Contacts / Companies / Entities list + "Link …" add panel,
// shared by the Add/Edit Unit dialog (contacts + entities) and the Contacts
// tab's Manage Links dialog (all three). Purely staged: every link, unlink,
// and role change only edits the `links` list the parent dialog owns —
// nothing is written to HubSpot until that dialog's Save. See linkStaging.js
// for the link shape.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";

import { getAPICall } from "../../../config/apiCalls";
import { KIND_CONFIG, contactSub } from "./linkStaging";

import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import { debounce } from "@mui/material";

import AddBusinessIcon from "@mui/icons-material/AddBusiness";
import AddIcon from "@mui/icons-material/Add";
import BusinessIcon from "@mui/icons-material/Business";
import CloseIcon from "@mui/icons-material/Close";
import DomainAddIcon from "@mui/icons-material/DomainAdd";
import DomainIcon from "@mui/icons-material/Domain";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonIcon from "@mui/icons-material/Person";

const KIND_ICONS = {
	contact: { icon: PersonIcon, add: PersonAddIcon },
	company: { icon: BusinessIcon, add: AddBusinessIcon },
	entity: { icon: DomainIcon, add: DomainAddIcon },
};

const KindIcon = ({ kind, ...rest }) => {
	const Icon = KIND_ICONS[kind]?.icon || PersonIcon;
	return <Icon {...rest} />;
};

const DISPLAY_ORDER = ["contact", "company", "entity"];

// ["Contact", "Company", "Entity"], "or" -> "Contact, Company, or Entity"
const joinList = (items, conjunction) =>
	items.length <= 2 ? items.join(` ${conjunction} `) : `${items.slice(0, -1).join(", ")}, ${conjunction} ${items[items.length - 1]}`;

const EMPTY_FORM = {
	name: "",
	email: "",
	phone: "",
	mobile: "",
	jobTitle: "",
	domain: "",
	type: "",
	entity_address_1: "",
	entity_street_address_2: "",
	entity_city: "",
	entity_state: "",
	zip_code: "",
};

// A dropdown when HubSpot gave us enumeration options, free text otherwise.
export const OptionsField = ({ label, options, value, onChange, disabled }) =>
	options.length > 0 ? (
		<TextField select label={label} size="small" fullWidth value={value} onChange={onChange} disabled={disabled}>
			<MenuItem value="">
				<em>None</em>
			</MenuItem>
			{options.map((t) => (
				<MenuItem key={t.value} value={t.value}>
					{t.label}
				</MenuItem>
			))}
		</TextField>
	) : (
		<TextField label={label} size="small" fullWidth value={value} onChange={onChange} disabled={disabled} />
	);

// Role dropdown for one kind. `allowNone` adds a "No role" choice — always
// for the Property (plain links are fine there), and for a Unit only when
// the record is already linked without a label.
export const RoleSelect = ({ roles, value, onChange, allowNone, disabled, className, label, helperText }) => (
	<TextField
		select
		size="small"
		label={label}
		className={className}
		value={value}
		onChange={onChange}
		disabled={disabled}
		helperText={helperText}
		SelectProps={{ displayEmpty: true }}
		InputLabelProps={label ? { shrink: true } : undefined}
	>
		{allowNone && (
			<MenuItem value="">
				<em>No role</em>
			</MenuItem>
		)}
		{roles.map((r) => (
			<MenuItem key={r.typeId} value={String(r.typeId)}>
				{r.label}
			</MenuItem>
		))}
	</TextField>
);

const LinkRow = ({ link, roles, requireRole, disabled, onToggle, onRoleChange }) => {
	const removed = link.status === "removed";
	const edited = link.status === "saved" && link.typeId !== link.origTypeId;
	const kindRoles = roles[link.kind] || [];
	return (
		<div className={`linkDialogLinkRow${link.status === "new" ? " isNew" : ""}${removed ? " isRemoved" : ""}`}>
			<KindIcon kind={link.kind} className="linkDialogLinkIcon" />
			<div className="linkDialogLinkText">
				<div className="linkDialogLinkNameRow">
					<span className="linkDialogLinkName">{link.name}</span>
					{link.status === "new" && <span className="linkDialogBadge new">New</span>}
					{edited && <span className="linkDialogBadge edited">Edited</span>}
					{removed && <span className="linkDialogBadge removed">Unlinking</span>}
				</div>
				{link.sub && <div className="linkDialogLinkSub">{link.sub}</div>}
			</div>
			{kindRoles.length > 0 && (
				<RoleSelect
					className="linkDialogRowRole"
					roles={kindRoles}
					value={link.typeId}
					onChange={(e) => onRoleChange(link.key, e.target.value)}
					allowNone={!requireRole || !link.origTypeId}
					disabled={disabled || removed}
				/>
			)}
			{removed ? (
				<Button size="small" className="linkDialogUndoButton" onClick={() => onToggle(link.key)} disabled={disabled}>
					Undo
				</Button>
			) : (
				<Tooltip arrow placement="top" title="Unlink">
					<span>
						<IconButton className="linkDialogUnlinkButton" onClick={() => onToggle(link.key)} disabled={disabled} aria-label="Unlink">
							<LinkOffIcon fontSize="small" />
						</IconButton>
					</span>
				</Tooltip>
			)}
		</div>
	);
};

/**
 * @param {string} title: section heading, e.g. "Contacts & Entities"
 * @param {string[]} kinds: which record kinds can be linked, in group order (e.g. ["entity", "contact"])
 * @param {string} searchEndpoint / searchParams: GET endpoint returning { results }; `q` is added
 * @param {Object} roles: { [kind]: [{ typeId, label }] } — a kind with none gets no role picker
 * @param {boolean} requireRole: every link needs a label (Units)
 * @param {Object} defaultRoleLabels: { [kind]: label } preselected in the add panel
 * @param {boolean} showJobTitle: New Contact form gets a Job Title field
 */
const LinkedRecordsSection = ({
	title,
	kinds,
	searchEndpoint,
	searchParams,
	links,
	roles,
	requireRole = false,
	defaultRoleLabels = {},
	entityTypes = [],
	companyTypes = [],
	showJobTitle = false,
	disabled,
	onAdd,
	onToggle,
	onRoleChange,
}) => {
	const { instance, accounts } = useMsal();

	const [adding, setAdding] = useState(false);
	const [query, setQuery] = useState("");
	const [results, setResults] = useState([]);
	const [searching, setSearching] = useState(false);
	const [candidate, setCandidate] = useState(null);
	const [createMode, setCreateMode] = useState(null); // null | kind
	const [addTypeId, setAddTypeId] = useState("");
	const [form, setForm] = useState(EMPTY_FORM);

	const addKind = createMode || candidate?.kind || null;
	const addKindRoles = addKind ? roles[addKind] || [] : [];

	// Preselect the add panel's role whenever what's being added changes kind.
	useEffect(() => {
		if (!addKind) {
			setAddTypeId("");
			return;
		}
		const list = roles[addKind] || [];
		const preferred = list.find((r) => r.label === defaultRoleLabels[addKind]) || (requireRole ? list[0] : null);
		setAddTypeId(preferred ? String(preferred.typeId) : "");
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [addKind, roles]);

	// Latest endpoint/params for the debounced search without re-creating it.
	const searchConfigRef = useRef({ searchEndpoint, searchParams });
	searchConfigRef.current = { searchEndpoint, searchParams };

	const runSearch = useMemo(
		() =>
			debounce((q) => {
				const { searchEndpoint: endpoint, searchParams: params } = searchConfigRef.current;
				getAPICall(instance, accounts[0], endpoint, { ...(params || {}), q })
					.then((res) => setResults(res.data.results || []))
					.catch(() => setResults([]))
					.finally(() => setSearching(false));
			}, 350),
		[instance, accounts],
	);
	useEffect(() => () => runSearch.clear(), [runSearch]);

	const setField = (key) => (e) => {
		const value = e.target.value;
		setForm((current) => ({ ...current, [key]: value }));
	};

	const resetAdd = () => {
		runSearch.clear();
		setQuery("");
		setResults([]);
		setSearching(false);
		setCandidate(null);
		setCreateMode(null);
		setForm(EMPTY_FORM);
	};

	const closeAdd = () => {
		resetAdd();
		setAdding(false);
	};

	const handleQueryChange = (e) => {
		const value = e.target.value;
		setQuery(value);
		if (!value.trim()) {
			runSearch.clear();
			setResults([]);
			setSearching(false);
			return;
		}
		setSearching(true);
		runSearch(value.trim());
	};

	// Starting a create carries over whatever was typed in the search box.
	const startCreate = (kind) => {
		setCreateMode(kind);
		setCandidate(null);
		setForm({ ...EMPTY_FORM, name: query.trim() });
	};

	const linkedKeys = new Set(links.map((l) => l.key));
	const visibleResults = results.filter((r) => !linkedKeys.has(`${r.kind}-${r.id}`));
	const showResults = Boolean(query.trim()) && !candidate;

	const optionLabel = (options, value) => options.find((t) => t.value === value)?.label || value;
	const roleMissing = requireRole && addKindRoles.length > 0 && !addTypeId;
	const cannotAdd = roleMissing || (createMode ? !form.name.trim() : !candidate);

	const buildNewLink = () => {
		const f = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v.trim()]));
		const base = { key: `new-${Date.now()}`, kind: createMode, id: null, name: f.name, typeId: addTypeId };
		if (createMode === "contact") {
			return {
				...base,
				sub: [f.jobTitle, contactSub(f.email, f.phone, f.mobile)].filter(Boolean).join(" · ") || "New contact",
				newFields: { fullName: f.name, email: f.email, phone: f.phone, mobilephone: f.mobile, ...(showJobTitle ? { jobtitle: f.jobTitle } : {}) },
			};
		}
		if (createMode === "company") {
			return {
				...base,
				sub: [f.type && optionLabel(companyTypes, f.type), f.domain, f.phone, "New company"].filter(Boolean).join(" · "),
				newFields: { name: f.name, domain: f.domain, phone: f.phone, type: f.type },
			};
		}
		const addressLine = [f.entity_address_1, f.entity_city, f.entity_state].filter(Boolean).join(", ");
		return {
			...base,
			sub: [f.type && optionLabel(entityTypes, f.type), addressLine, "New entity"].filter(Boolean).join(" · "),
			newFields: {
				entity_name: f.name,
				entity_type: f.type,
				entity_address_1: f.entity_address_1,
				entity_street_address_2: f.entity_street_address_2,
				entity_city: f.entity_city,
				entity_state: f.entity_state,
				zip_code: f.zip_code,
				phone_number: f.phone,
			},
		};
	};

	const addToList = () => {
		if (cannotAdd) return;
		onAdd(
			createMode
				? buildNewLink()
				: {
					key: `${candidate.kind}-${candidate.id}`,
					kind: candidate.kind,
					id: candidate.id,
					existingId: candidate.id,
					name: candidate.name,
					sub: candidate.sub,
					typeId: addTypeId,
				},
		);
		resetAdd();
	};

	const groups = kinds
		.map((kind) => ({ kind, rows: links.filter((l) => l.kind === kind) }))
		.filter((g) => g.rows.length > 0);
	const linkCount = links.filter((l) => l.status !== "removed").length;
	// Wording always reads contact, company, entity — regardless of group order.
	const displayKinds = DISPLAY_ORDER.filter((k) => kinds.includes(k));
	const singulars = displayKinds.map((k) => KIND_CONFIG[k].label);
	const plurals = displayKinds.map((k) => KIND_CONFIG[k].plural.toLowerCase());

	return (
		<div className="linkDialogOwners">
			<div className="linkDialogSectionHeader">
				<div className="linkDialogSectionTitle">{title}</div>
				<span className="linkDialogCountPill">{linkCount}</span>
			</div>

			<div className="linkDialogLinkList">
				{links.length === 0 && <div className="linkDialogLinkEmpty">No {joinList(plurals, "or")} linked</div>}
				{groups.map((g) => (
					<React.Fragment key={g.kind}>
						<div className="linkDialogGroupHeader">
							{KIND_CONFIG[g.kind].plural} <span className="linkDialogGroupCount">{g.rows.length}</span>
						</div>
						{g.rows.map((l) => (
							<LinkRow
								key={l.key}
								link={l}
								roles={roles}
								requireRole={requireRole}
								disabled={disabled}
								onToggle={onToggle}
								onRoleChange={onRoleChange}
							/>
						))}
					</React.Fragment>
				))}
			</div>

			{!adding ? (
				<button type="button" className="linkDialogLinkButton" onClick={() => setAdding(true)} disabled={disabled}>
					<AddIcon fontSize="small" />
					Link {joinList(singulars, "or")}
				</button>
			) : (
				<div className="linkDialogAddPanel">
					<div className="linkDialogAddHeader">
						<div className="linkDialogAddTitle">
							{createMode ? `New ${KIND_CONFIG[createMode].label}` : `Link ${joinList(singulars, "or")}`}
						</div>
						<Tooltip arrow placement="top" title="Cancel">
							<IconButton size="small" onClick={closeAdd} aria-label="Cancel">
								<CloseIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					</div>

					{!createMode && (
						<>
							<div className="linkDialogSearchWrap">
								{!candidate ? (
									<TextField
										size="small"
										fullWidth
										autoFocus
										placeholder={`Search ${joinList(plurals, "and")} by name${kinds.includes("contact") ? " or email" : ""}`}
										value={query}
										onChange={handleQueryChange}
										InputProps={{ endAdornment: searching ? <CircularProgress color="inherit" size={16} /> : null }}
									/>
								) : (
									<div className="linkDialogCandidate">
										<KindIcon kind={candidate.kind} className="linkDialogLinkIcon" />
										<div className="linkDialogLinkText">
											<div className="linkDialogLinkName">{candidate.name}</div>
											{candidate.sub && <div className="linkDialogLinkSub">{candidate.sub}</div>}
										</div>
										<Tooltip arrow placement="top" title="Change">
											<IconButton size="small" onClick={() => setCandidate(null)} aria-label="Change">
												<CloseIcon fontSize="small" />
											</IconButton>
										</Tooltip>
									</div>
								)}

								{showResults && (
									<div className="linkDialogResults">
										{visibleResults.map((r) => (
											<div
												key={`${r.kind}-${r.id}`}
												className="linkDialogResult"
												role="option"
												aria-selected={false}
												onClick={() => {
													setCandidate(r);
													setQuery("");
													setResults([]);
												}}
											>
												<KindIcon kind={r.kind} className="linkDialogLinkIcon" />
												<div className="linkDialogLinkText">
													<div className="linkDialogResultName">{r.name}</div>
													{r.sub && <div className="linkDialogLinkSub">{r.sub}</div>}
												</div>
												<span className="linkDialogResultType">{KIND_CONFIG[r.kind]?.label}</span>
											</div>
										))}
										{!searching && visibleResults.length === 0 && <div className="linkDialogNoResults">No Results Found</div>}
										{searching && visibleResults.length === 0 && <div className="linkDialogNoResults">Searching…</div>}
									</div>
								)}
							</div>

							{!candidate && (
								<div className="linkDialogCreateRow">
									<span>Not in the system yet?</span>
									{/* One non-wrapping group, so the buttons stay on a line together. */}
									<div className="linkDialogCreateButtons">
										{displayKinds.map((k) => {
											const AddKindIcon = KIND_ICONS[k].add;
											return (
												<Button key={k} variant="outlined" size="small" startIcon={<AddKindIcon />} onClick={() => startCreate(k)}>
													New {KIND_CONFIG[k].label}
												</Button>
											);
										})}
									</div>
								</div>
							)}
						</>
					)}

					{createMode === "contact" && (
						<div className="linkDialogGrid2">
							<TextField className="linkDialogSpanAll" label="Full Name" size="small" fullWidth autoFocus value={form.name} onChange={setField("name")} />
							{showJobTitle && (
								<TextField className="linkDialogSpanAll" label="Job Title" size="small" fullWidth value={form.jobTitle} onChange={setField("jobTitle")} />
							)}
							<TextField className="linkDialogSpanAll" label="Email" size="small" fullWidth value={form.email} onChange={setField("email")} />
							<TextField label="Phone" size="small" fullWidth value={form.phone} onChange={setField("phone")} />
							<TextField label="Mobile" size="small" fullWidth value={form.mobile} onChange={setField("mobile")} />
						</div>
					)}

					{createMode === "company" && (
						<div className="linkDialogGridEntity">
							<TextField label="Company Name" size="small" fullWidth autoFocus value={form.name} onChange={setField("name")} />
							<OptionsField label="Type" options={companyTypes} value={form.type} onChange={setField("type")} />
							<TextField label="Domain" size="small" fullWidth placeholder="example.com" value={form.domain} onChange={setField("domain")} />
							<TextField label="Phone" size="small" fullWidth value={form.phone} onChange={setField("phone")} />
						</div>
					)}

					{createMode === "entity" && (
						<div className="linkDialogGridEntity">
							<TextField label="Legal Name" size="small" fullWidth autoFocus value={form.name} onChange={setField("name")} />
							<OptionsField label="Entity Type" options={entityTypes} value={form.type} onChange={setField("type")} />
							<TextField className="linkDialogSpanAll" label="Address 1" size="small" fullWidth value={form.entity_address_1} onChange={setField("entity_address_1")} />
							<TextField className="linkDialogSpanAll" label="Address 2" size="small" fullWidth value={form.entity_street_address_2} onChange={setField("entity_street_address_2")} />
							<div className="linkDialogCityRow linkDialogSpanAll">
								<TextField label="City" size="small" fullWidth value={form.entity_city} onChange={setField("entity_city")} />
								<TextField label="State" size="small" fullWidth value={form.entity_state} onChange={setField("entity_state")} />
								<TextField label="Zip" size="small" fullWidth value={form.zip_code} onChange={setField("zip_code")} />
							</div>
							<TextField label="Phone" size="small" fullWidth value={form.phone} onChange={setField("phone")} />
						</div>
					)}

					<div className="linkDialogAddFooter">
						{(!addKind || addKindRoles.length > 0) && (
							<RoleSelect
								label="Role"
								className="linkDialogAddRole"
								roles={addKindRoles}
								value={addTypeId}
								onChange={(e) => setAddTypeId(e.target.value)}
								allowNone={!requireRole}
								disabled={!addKind}
								helperText={!addKind ? `Pick a ${joinList(singulars, "or").toLowerCase()} first` : undefined}
							/>
						)}
						{addKind && addKindRoles.length === 0 && <div className="linkDialogAddRole" />}
						{createMode && (
							<Button variant="text" onClick={() => setCreateMode(null)}>
								Back
							</Button>
						)}
						<Button variant="contained" disableElevation disabled={cannotAdd} onClick={addToList}>
							Add to List
						</Button>
					</div>
				</div>
			)}
		</div>
	);
};

export default LinkedRecordsSection;
